import type { PrimaryTabId, SecondaryTabId } from '~/utils/creationIntentRegistry'
import type { CustomShotItem } from '~/utils/customShot'
import { formatModelName } from '~/utils/inspiration'
import type { AlmightyReferenceKind } from '~/utils/almightyReference'
import {
    cloneAlmightyPromptMentionTokens,
    type AlmightyPromptMentionToken,
} from '~/utils/almightyMention'
import { isAlmightyReferenceSupported } from '~/utils/videoModelCapabilities'

export const WORKBENCH_INSPIRATION_PREFILL_KEY =
    'wery_workbench_inspiration_prefill_v1'

export type WorkbenchInspirationPrefillV1 = {
    v: 1
    ts: number
    /** Original inspiration item (kept for debugging / future expansion). */
    item?: any

    // tabs
    mainCategorySelected: PrimaryTabId | null
    specificCategorySelected: SecondaryTabId | null

    // image/video generate
    positive_prompts?: string
    selectedModelKey?: string
    selectedOutputCount?: number
    selectedAspectRatio?: string
    selectedDuration?: number
    selectedResolution?: string
    generate_type?: 'standard' | 'batch' | 'single'
    model_list?: string[]
    use_web_search?: boolean
    public_visible?: boolean
    copyright_protection?: boolean
    generate_audio?: boolean
    uploadImageList?: string[]
    intelligent_prompt?: string
    custom_shot_enabled?: boolean
    custom_shot_mode?: boolean
    multi_prompt?: CustomShotItem[]
    active_shot_index?: number
    mode?: string
    almighty_mode?: boolean
    almighty_images?: string[]
    almighty_videos?: string[]
    almighty_audios?: string[]
    almighty_reference_items?: Array<{
        id: string
        kind: AlmightyReferenceKind
        url: string
        name?: string
        duration?: number
    }>
    almighty_prompt_tokens?: AlmightyPromptMentionToken[]

    // music
    music?: {
        /** VOICE / PURE_MUSIC */
        secondary: 'VOICE' | 'PURE_MUSIC'
        music_lyrics?: string
        music_inputText?: string
        voice_tags?: string[] // keys only; PcWorkbench will map to localized labels
        voice_gender?: 'BOY_VOICE' | 'GIRL_VOICE'
        reference_audio?: string
        audio_name?: string
    }

    // templates
    template?: {
        templateType: 'IMAGE' | 'VIDEO'
        selectedTemplate?: any | null
        selectedTemplateConfig?: any | null
        templateUploadedImages?: string[]
    }
}

type NormalizedAlmightyReferenceItem = {
    id: string
    kind: AlmightyReferenceKind
    url: string
    name?: string
    duration?: number
}

const hasAlmightyItems = (value: unknown) =>
    Array.isArray(value) && value.length > 0

const hasWorkbenchAlmightyPayload = (value: any) => {
    const payload = value?.param ?? value
    return Boolean(
        payload?.mode === 'ALMIGHTY_REFERENCE' ||
        payload?.almighty_mode === true ||
        hasAlmightyItems(payload?.almighty_reference_items) ||
        hasAlmightyItems(payload?.almighty_images) ||
        hasAlmightyItems(payload?.almighty_videos) ||
        hasAlmightyItems(payload?.almighty_audios)
    )
}

const isWorkbenchSeedance20Model = (value: any) => {
    const rawCandidates = [
        value?.selectedModelKey,
        value?.selectedModelParentKey,
        value?.model,
        value?.item?.model,
        value?.item?.param?.model,
        value?.param?.model,
    ]
    return rawCandidates.some(item =>
        typeof item === 'string' &&
        !!item.trim() &&
        isAlmightyReferenceSupported(item.trim())
    )
}

export const hasWorkbenchAlmightyReference = (value: any) => {
    return hasWorkbenchAlmightyPayload(value) && isWorkbenchSeedance20Model(value)
}

const normalizeAlmightyReferenceItems = (param: any): NormalizedAlmightyReferenceItem[] => {
    if (Array.isArray(param?.almighty_reference_items)) {
        return param.almighty_reference_items
            .filter((item: any) =>
                item &&
                ['image', 'video', 'audio'].includes(item.kind) &&
                typeof item.url === 'string' &&
                !!item.url
            )
            .map((item: any, index: number) => ({
                id:
                    typeof item.id === 'string' && item.id
                        ? item.id
                        : `inspiration-almighty-${index}`,
                kind: item.kind as AlmightyReferenceKind,
                url: item.url,
                name: typeof item.name === 'string' ? item.name : undefined,
                duration:
                    typeof item.duration === 'number' ? item.duration : undefined,
            }))
    }

    const toUrlList = (list: unknown) =>
        (Array.isArray(list) ? list : [])
            .map((item: any) => {
                if (typeof item === 'string') return item
                if (typeof item?.url === 'string' && item.url) return item.url
                if (typeof item?.src === 'string' && item.src) return item.src
                if (typeof item?.file_url === 'string' && item.file_url) return item.file_url
                return ''
            })
            .filter((url: string) => !!url)

    const imageUrls = toUrlList(param?.images).length > 0
        ? toUrlList(param?.images)
        : toUrlList(param?.almighty_images).length > 0
            ? toUrlList(param?.almighty_images)
            : toUrlList(param?.pic_urls)
    const videoUrls = toUrlList(param?.videos).length > 0
        ? toUrlList(param?.videos)
        : toUrlList(param?.almighty_videos).length > 0
            ? toUrlList(param?.almighty_videos)
            : toUrlList(param?.video_urls)
    const audioUrls = toUrlList(param?.audios).length > 0
        ? toUrlList(param?.audios)
        : toUrlList(param?.almighty_audios).length > 0
            ? toUrlList(param?.almighty_audios)
            : toUrlList(param?.audio_urls)

    return [
        ...imageUrls.map((url: string, index: number) => ({
            id: `inspiration-almighty-image-${index}`,
            kind: 'image' as const,
            url,
        })),
        ...videoUrls.map((url: string, index: number) => ({
            id: `inspiration-almighty-video-${index}`,
            kind: 'video' as const,
            url,
        })),
        ...audioUrls.map((url: string, index: number) => ({
            id: `inspiration-almighty-audio-${index}`,
            kind: 'audio' as const,
            url,
        })),
    ]
}

function normalizeModelKey(raw: any) {
    const name = typeof raw === 'string' ? raw : ''
    // Keep aligned with tool pages:
    // - base normalization via formatModelName
    // - special-case legacy alias (SEEDREAM -> SEEDREAM_4)
    let next = name ? formatModelName(name) : ''
    if (next === 'SEEDREAM') next = 'SEEDREAM_4'
    return next
}

const normalizePositiveNumber = (value: unknown) => {
    const num =
        typeof value === 'number'
            ? value
            : typeof value === 'string' && value.trim()
                ? Number(value)
                : NaN
    return Number.isFinite(num) && num > 0 ? num : undefined
}

function isChatPath(pathname: string) {
    // /chat or /xx/chat
    return /^\/(?:[a-z]{2}\/)?chat(?:\/|$)/i.test(pathname || '')
}

export function buildWorkbenchPrefillFromInspirationItem(
    item: any,
    ctx?: { pathname?: string }
): WorkbenchInspirationPrefillV1 | null {
    if (!item) return null

    const type = String(item?.type || '')
    const param = item?.param || {}
    const music = item?.music || {}

    // base envelope
    const base: WorkbenchInspirationPrefillV1 = {
        v: 1,
        ts: Date.now(),
        item,
        mainCategorySelected: null,
        specificCategorySelected: null,
    }

    // ========== map tabs by inspiration item.type ==========
    if (type === 'TEXT_TO_IMG') {
        base.mainCategorySelected = 'IMAGE_GENERATE'
        base.specificCategorySelected = 'TEXT_TO_IMAGE'
    } else if (type === 'IMG_TO_IMG') {
        base.mainCategorySelected = 'IMAGE_GENERATE'
        base.specificCategorySelected = 'IMAGE_TO_IMAGE'
    } else if (type === 'TEXT_TO_VIDEO') {
        base.mainCategorySelected = 'VIDEO_GENERATE'
        base.specificCategorySelected = 'TEXT_TO_VIDEO'
    } else if (type === 'IMG_TO_VIDEO') {
        base.mainCategorySelected = 'VIDEO_GENERATE'
        base.specificCategorySelected = 'IMAGE_TO_VIDEO'
    } else if (type === 'IMAGE_EFFECT_TEMPLATE_CREATION') {
        base.mainCategorySelected = 'IMAGE_TEMPLATE'
        base.specificCategorySelected = null
        const selectedTemplate =
            (param as any)?.template ??
            (item as any)?.template ??
            ((param as any)?.template_id || (param as any)?.template_path
                ? {
                      id: (param as any)?.template_id,
                      name:
                          (param as any)?.template_name ??
                          (item as any)?.name ??
                          (item as any)?.title ??
                          '',
                      cover_url:
                          (param as any)?.template_cover_url ??
                          (item as any)?.cover_url ??
                          (item as any)?.cover ??
                          '',
                      path_url:
                          (param as any)?.template_path ??
                          (param as any)?.template_slug ??
                          (param as any)?.template_path_url ??
                          '',
                  }
                : null)
        base.template = {
            templateType: 'IMAGE',
            selectedTemplate,
            selectedTemplateConfig:
                (param as any)?.template_config ??
                (param as any)?.templateConfig ??
                (param as any)?.config ??
                (item as any)?.config ??
                null,
            templateUploadedImages: Array.isArray(param?.pic_urls)
                ? param.pic_urls
                : [],
        }
    } else if (type === 'VIDEO_EFFECT_TEMPLATE_CREATION') {
        base.mainCategorySelected = 'VIDEO_TEMPLATE'
        base.specificCategorySelected = null
        const selectedTemplate =
            (param as any)?.template ??
            (item as any)?.template ??
            ((param as any)?.template_id || (param as any)?.template_path
                ? {
                      id: (param as any)?.template_id,
                      name:
                          (param as any)?.template_name ??
                          (item as any)?.name ??
                          (item as any)?.title ??
                          '',
                      cover_url:
                          (param as any)?.template_cover_url ??
                          (item as any)?.cover_url ??
                          (item as any)?.cover ??
                          '',
                      path_url:
                          (param as any)?.template_path ??
                          (param as any)?.template_slug ??
                          (param as any)?.template_path_url ??
                          '',
                  }
                : null)
        base.template = {
            templateType: 'VIDEO',
            selectedTemplate,
            selectedTemplateConfig:
                (param as any)?.template_config ??
                (param as any)?.templateConfig ??
                (param as any)?.config ??
                (item as any)?.config ??
                null,
            templateUploadedImages: Array.isArray(param?.pic_urls)
                ? param.pic_urls
                : [],
        }
    } else if (type === 'MUSIC_CREATION_GENERATION') {
        base.mainCategorySelected = 'MUSIC_GENERATE'
        // param.type: VOCAL_SONG / ONLY_MUSIC
        const musicType = String(param?.type || '')
        const secondary =
            musicType === 'ONLY_MUSIC' ? 'PURE_MUSIC' : ('VOICE' as const)
        base.specificCategorySelected = secondary
        base.music = {
            secondary,
            music_lyrics:
                typeof music?.generated_lyrics === 'string'
                    ? music.generated_lyrics
                    : typeof param?.lyrics === 'string'
                        ? param.lyrics
                        : undefined,
            music_inputText:
                typeof music?.music_title === 'string'
                    ? music.music_title
                    : typeof param?.prompt === 'string'
                        ? param.prompt
                        : undefined,
            voice_tags: Array.isArray((item as any)?.styles)
                ? ((item as any).styles as any[]).filter((s) => typeof s === 'string')
                : Array.isArray(param?.styles)
                    ? (param.styles as any[]).filter((s) => typeof s === 'string')
                    : [],
            voice_gender:
                (item as any)?.gender === 'm'
                    ? 'BOY_VOICE'
                    : (item as any)?.gender === 'f'
                        ? 'GIRL_VOICE'
                        : undefined,
            reference_audio:
                typeof (param as any)?.audio_urls?.[0] === 'string'
                    ? (param as any).audio_urls[0]
                    : (item as any)?.music?.reference_audio || undefined,
            audio_name: 
                typeof (param as any)?.name === 'string' 
                    ? (param as any).name 
                    : (item as any)?.music?.audio_name || undefined,
        }
    } else {
        // Unsupported types: still allow only writing the raw item for future handling,
        // but do not apply anything to workbench.
        return null
    }

    // ========== fill common fields for image/video ==========
    if (
        base.mainCategorySelected === 'IMAGE_GENERATE' ||
        base.mainCategorySelected === 'VIDEO_GENERATE'
    ) {
        const isAlmightyReference = hasWorkbenchAlmightyReference(param)
        const shotType = typeof param?.shot_type === 'string'
            ? param.shot_type.toLowerCase()
            : ''
        const multiPrompt = Array.isArray(param?.multi_prompt)
            ? param.multi_prompt.map((item: any) => ({
                prompt: typeof item?.prompt === 'string' ? item.prompt : '',
                duration:
                    typeof item?.duration === 'number'
                        ? item.duration
                        : Number(item?.duration || 1) || 1,
            }))
            : []
        const activeShotIndexRaw = param?.active_shot_index
        const activeShotIndex = typeof activeShotIndexRaw === 'number'
            ? activeShotIndexRaw
            : Number(activeShotIndexRaw)
        const hasMultiPrompt = multiPrompt.length > 0

        base.positive_prompts = typeof param?.prompt === 'string' ? param.prompt : ''
        base.selectedModelKey = normalizeModelKey(param?.model || item?.model)
        base.selectedOutputCount =
            typeof param?.output_num === 'number' ? param.output_num : undefined
        base.selectedAspectRatio =
            typeof param?.aspect_ratio === 'string' ? param.aspect_ratio : undefined
        base.selectedDuration =
            normalizePositiveNumber(param?.duration) ??
            normalizePositiveNumber(param?.t)
        base.selectedResolution =
            typeof param?.resolution === 'string' ? param.resolution : undefined
        base.copyright_protection = Boolean(param?.copyright_protection)
        base.public_visible =
            typeof param?.public_visible === 'boolean'
                ? param.public_visible
                : undefined
        base.use_web_search =
            typeof param?.use_web_search === 'boolean' ? param.use_web_search : undefined
        base.generate_audio =
            typeof param?.generate_audio === 'boolean'
                ? param.generate_audio
                : undefined
        if (hasMultiPrompt || shotType === 'intelligent' || shotType === 'customize') {
            base.custom_shot_enabled = true
            base.custom_shot_mode = hasMultiPrompt || shotType === 'customize'
        }
        if (typeof param?.intelligent_prompt === 'string') {
            base.intelligent_prompt = param.intelligent_prompt
        } else if (!hasMultiPrompt && shotType === 'intelligent' && typeof param?.prompt === 'string') {
            base.intelligent_prompt = param.prompt
        }
        if (multiPrompt.length > 0) {
            base.multi_prompt = multiPrompt
        }
        if (Number.isFinite(activeShotIndex) && activeShotIndex >= 0) {
            base.active_shot_index = Math.floor(activeShotIndex)
        }

        if (isAlmightyReference && base.specificCategorySelected === 'IMAGE_TO_VIDEO') {
            const almightyReferenceItems = normalizeAlmightyReferenceItems(param)
            base.mode = 'ALMIGHTY_REFERENCE'
            base.almighty_mode = true
            base.almighty_reference_items = almightyReferenceItems
            base.almighty_images = almightyReferenceItems
                .filter((item: NormalizedAlmightyReferenceItem) => item.kind === 'image')
                .map((item: NormalizedAlmightyReferenceItem) => item.url)
            base.almighty_videos = almightyReferenceItems
                .filter((item: NormalizedAlmightyReferenceItem) => item.kind === 'video')
                .map((item: NormalizedAlmightyReferenceItem) => item.url)
            base.almighty_audios = almightyReferenceItems
                .filter((item: NormalizedAlmightyReferenceItem) => item.kind === 'audio')
                .map((item: NormalizedAlmightyReferenceItem) => item.url)
            base.almighty_prompt_tokens = cloneAlmightyPromptMentionTokens(
                Array.isArray(param?.almighty_prompt_tokens)
                    ? param.almighty_prompt_tokens
                    : [],
                typeof param?.prompt === 'string' ? param.prompt : ''
            )
            base.uploadImageList = []
        }

        // image-to-image / image-to-video: use pic_urls as workbench upload list
        if (
            base.specificCategorySelected === 'IMAGE_TO_IMAGE' ||
            base.specificCategorySelected === 'IMAGE_TO_VIDEO'
        ) {
            if (!isAlmightyReference) {
                base.uploadImageList = Array.isArray(param?.pic_urls)
                    ? param.pic_urls
                    : []
            }
        }
    }

    // For chat-page click, we may want to keep the page as-is (no navigation),
    // but it doesn't change the prefill payload itself.
    void ctx
    return base
}

export function writeWorkbenchInspirationPrefillToStorage(
    prefill: WorkbenchInspirationPrefillV1
) {
    if (!import.meta.client || typeof window === 'undefined') return
    try {
        window.localStorage.setItem(
            WORKBENCH_INSPIRATION_PREFILL_KEY,
            JSON.stringify(prefill)
        )
    } catch (e) {
        console.warn('[workbenchInspirationPrefill] write failed:', e)
    }
}

export function isInChatPageRuntime() {
    if (!import.meta.client || typeof window === 'undefined') return false
    return isChatPath(window.location?.pathname || '')
}

